import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { DateValue } from '@react-types/calendar';
import {
  Button,
  DatePicker,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  WeightField,
  type Weight,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { handleActionableError } from '~/modules/trade/utils/errorHandling';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

interface LoadMarkAsShippedDialogProps {
  onMarkAsShipped: (
    shippedDate: CalendarDate | undefined,
    lightWeight: Weight | null,
    heavyWeight: Weight | null,
  ) => Promise<void>;
  onCancel?: () => void;
  shippedDate?: CalendarDate | undefined;
}

export const LoadMarkAsShippedDialog: React.FC<
  LoadMarkAsShippedDialogProps
> = ({ onMarkAsShipped, onCancel, shippedDate: initialShippedDate }) => {
  const t = useTranslations();
  const toast = useToast();
  const [, navigate] = useModuleLocation();
  const [shippedDate, setShippedDate] = React.useState<DateValue | null>(
    (initialShippedDate as unknown as DateValue) ?? null,
  );
  const [lightWeight, setLightWeight] = React.useState<Weight | null>(null);
  const [heavyWeight, setHeavyWeight] = React.useState<Weight | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onMarkAsShipped(
        (shippedDate as unknown as CalendarDate) ?? undefined,
        lightWeight,
        heavyWeight,
      );
    } catch (error) {
      handleActionableError(error, toast, navigate);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = shippedDate;

  return (
    <DialogOverlay isOpen className="@LoadMarkAsShippedDialog">
      <DialogContent>
        <DialogHeader>
          <div className="text-base font-semibold">
            {t.loadStatusTransition.markAsShippedPopover.markAsShipped}
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-4">
          <DatePicker
            value={shippedDate}
            onChange={setShippedDate}
            className="w-full"
            label={t.loadStatusTransition.markAsShippedPopover.shippedDate}
            isRequired={true}
            isDisabled={false}
          />
          <WeightField
            weightValue={lightWeight}
            onWeightValueChange={setLightWeight}
            defaultUnit="Pounds"
            unitLocked
            className="w-full"
            label={t.loadStatusTransition.markAsShippedPopover.lightWeight}
            isRequired={true}
            isDisabled={false}
          />
          <WeightField
            weightValue={heavyWeight}
            onWeightValueChange={setHeavyWeight}
            defaultUnit="Pounds"
            unitLocked
            className="w-full"
            label={t.loadStatusTransition.markAsShippedPopover.heavyWeight}
            isRequired={true}
            isDisabled={false}
          />
          <div className="flex justify-end gap-2 py-4">
            <Button variant="text" color="neutral" onPress={onCancel}>
              {t.loadStatusTransition.markAsShippedPopover.discard}
            </Button>
            <Button
              variant="solid"
              onPress={handleConfirm}
              isLoading={isLoading}
              isDisabled={!isFormValid}
            >
              {t.loadStatusTransition.markAsShippedPopover.markAsShipped}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
